import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Link as LinkIcon, Link2Off, RefreshCw, CheckCircle2,
         XCircle, Loader2, ExternalLink, ClipboardPaste,
         Briefcase, TrendingUp, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const iso = (s) => s ? new Date(s).toLocaleString() : "—";

/**
 * Vinci ⇄ Zoho Recruit admin panel · connect status + applications feed.
 */
export function AdminCareersPanel({ api }) {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pasteCode, setPasteCode] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, statsResp] = await Promise.all([
        api.get("/admin/zoho-recruit/status"),
        api.get("/admin/zoho-recruit/applications?days=30").catch(() => ({ data: null })),
      ]);
      setStatus(s);
      setStats(statsResp?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Zoho Recruit status");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/admin/zoho-recruit/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Zoho Recruit connect");
    } finally { setBusy(""); }
  };

  const exchange = async () => {
    if (!pasteCode.trim()) return;
    setBusy("exchange");
    try {
      await api.post("/admin/zoho-recruit/exchange-code", { code: pasteCode.trim() });
      toast.success("Zoho Recruit connected · refresh token stored");
      setPasteCode(""); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code exchange failed");
    } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Recruit? The Careers page will stop showing live openings.")) return;
    setBusy("disconnect");
    try {
      await api.post("/admin/zoho-recruit/disconnect", {});
      toast.info("Zoho Recruit disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(""); }
  };

  const refreshJobs = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post("/admin/zoho-recruit/refresh-jobs", {});
      toast.success(`Jobs feed refreshed · ${data.count} live openings`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setBusy(""); }
  };

  if (loading && !status) {
    return (
      <div className="py-16 flex justify-center" data-testid="admin-careers-loading">
        <Loader2 className="w-6 h-6 text-[#B87333] animate-spin" />
      </div>
    );
  }

  const connected = status?.connected;
  const configured = status?.configured;

  return (
    <div className="space-y-6" data-testid="admin-careers-panel">
      {/* HEADER */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A6A1A] mb-2">Vinci ⇄ Zoho Recruit</div>
            <h2 className="font-serif text-2xl text-[#F5F5F0]">Careers portal</h2>
            <p className="text-sm text-[#C9C9C2] mt-2 max-w-xl">
              Live job openings + candidate applications sync directly with your Zoho Recruit tenant.
              The public <span className="text-[#F6E4A6]">/careers</span> page reads this feed and writes every submission back into the matching Job Opening.
            </p>
          </div>
          <div>
            {connected ? (
              <span className="inline-flex items-center gap-2 text-[#2FB999] font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border border-[#2FB999]/40">
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-[#C0392B] font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border border-[#C0392B]/40">
                <XCircle className="w-3.5 h-3.5" /> Not connected
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <StatusRow label="Configured" value={configured ? "Yes" : "No — set env keys"} />
          <StatusRow label="API base" value={status?.api_base || "—"} mono />
          <StatusRow label="Connected at" value={iso(status?.connected_at)} />
        </div>

        {!configured && (
          <div className="mt-4 border border-[#D8B23E]/30 bg-[#D8B23E]/5 px-4 py-3 text-xs text-[#F6E4A6] font-body">
            Zoho Recruit not configured. Set <span className="font-mono">ZOHO_RECRUIT_CLIENT_ID</span> and <span className="font-mono">ZOHO_RECRUIT_CLIENT_SECRET</span> in <span className="font-mono">/app/backend/.env</span>.
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {!connected ? (
            <>
              <Button data-testid="careers-connect-btn" onClick={connect} disabled={!configured || busy}>
                {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                Connect Zoho Recruit
              </Button>
              <div className="flex-1 flex items-center gap-2 min-w-[280px]">
                <Input
                  data-testid="careers-paste-code"
                  value={pasteCode}
                  onChange={(e) => setPasteCode(e.target.value)}
                  placeholder="Or paste a self-client code here…"
                />
                <Button data-testid="careers-exchange-btn"
                        variant="secondary" onClick={exchange}
                        disabled={!pasteCode.trim() || busy}>
                  {busy === "exchange" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button data-testid="careers-refresh-jobs" variant="outline" onClick={refreshJobs} disabled={busy}>
                {busy === "refresh" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh jobs feed
              </Button>
              <a
                href="/careers" target="_blank" rel="noreferrer"
                data-testid="careers-open-public"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-[#B87333] font-grotesk text-xs uppercase tracking-[0.2em] transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open public /careers
              </a>
              <Button data-testid="careers-disconnect-btn" variant="destructive"
                      onClick={disconnect} disabled={busy}>
                {busy === "disconnect" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2Off className="w-4 h-4 mr-2" />}
                Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {/* APPLICATIONS TILE */}
      <div className="border border-white/10 bg-black/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A6A1A] mb-1">Applications received</div>
            <h3 className="font-serif text-xl text-[#F5F5F0]">Career desk activity</h3>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Users} label="Total lifetime"
                     value={stats?.total ?? "—"} testid="careers-stat-total" />
          <StatCard icon={TrendingUp} label={`Last ${stats?.recent_days || 30} days`}
                     value={stats?.recent ?? "—"} testid="careers-stat-recent" />
          <StatCard icon={Briefcase} label="Live openings"
                     value={"→ /careers"} testid="careers-stat-jobs" small />
        </div>

        <div className="text-xs font-grotesk uppercase tracking-[0.28em] text-[#8A6A1A] mb-3">Recent applicants</div>
        {(stats?.latest || []).length === 0 ? (
          <div className="py-10 text-center text-[#8A8A82] font-body text-sm border border-dashed border-white/10">
            No applications yet. Once the Careers page starts converting, they'll land here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="careers-applications-table">
              <thead>
                <tr className="text-[#8A6A1A] font-grotesk text-[10px] uppercase tracking-[0.28em] border-b border-white/10">
                  <th className="text-left py-3">Applicant</th>
                  <th className="text-left py-3">Job</th>
                  <th className="text-left py-3">Received</th>
                  <th className="text-left py-3">Resume</th>
                </tr>
              </thead>
              <tbody className="text-[#C9C9C2] font-body">
                {(stats?.latest || []).slice(0, 15).map((a) => (
                  <tr key={a.candidate_id || a.email + a.created_at} className="border-b border-white/5">
                    <td className="py-3">
                      <div className="text-[#F5F5F0]">
                        {(a.first_name || "") + " " + (a.last_name || "")}
                      </div>
                      <div className="text-[#8A8A82] text-xs flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3 h-3" />{a.email}
                      </div>
                    </td>
                    <td className="py-3">{a.job_title || <span className="text-[#8A8A82]">—</span>}</td>
                    <td className="py-3 text-xs">{iso(a.created_at)}</td>
                    <td className="py-3 text-xs">
                      {a.resume_uploaded ? (
                        <span className="text-[#2FB999]">✓ Uploaded</span>
                      ) : a.queued ? (
                        <span className="text-[#D8B23E]">Queued</span>
                      ) : (
                        <span className="text-[#C0392B]" title={a.resume_error}>✗ Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function StatusRow({ label, value, mono }) {
  return (
    <div className="border-l border-[#B87333]/30 pl-3">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A]">{label}</div>
      <div className={`mt-1 text-sm text-[#F5F5F0] break-all ${mono ? "font-mono text-xs" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, small }) {
  return (
    <div className="border border-white/10 bg-black/20 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#8A6A1A] mb-3">
        <Icon className="w-4 h-4" />
        <span className="font-grotesk text-[9px] uppercase tracking-[0.28em]">{label}</span>
      </div>
      <div className={`font-serif text-[#F5F5F0] ${small ? "text-lg" : "text-3xl"}`}>{value}</div>
    </div>
  );
}
